import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { ErrorState } from '../components/guidance/error-state';
import { PageHeader } from '../components/layout/app-shell';
import { AdvancedBuilder } from '../components/report-document/advanced-builder';
import { ReportDocumentView } from '../components/report-document/document';
import { Button } from '../components/ui/button';
import type { ReportDoc } from '../lib/report-document';
import { useReport, useReportTemplates } from '../lib/reports';
import { useSchema } from '../lib/schema';

// One report (PLAN §10.1, §12.7 screen 8; task 31; Report v2). A saved report is a document, read and edited block
// by block, with the question builder, the results, exports and schedules in its Advanced drawer. A new, unsaved
// report (`/reports/new?template=…`, the Advanced path) is today's builder page.

const ULID = /^[0-9A-Z]{26}$/;

export const Route = createFileRoute('/reports/$id')({
  validateSearch: (s: Record<string, unknown>): { template?: string; run?: string; draft?: string; advanced?: boolean } => ({
    ...(typeof s.template === 'string' && s.template ? { template: s.template } : {}),
    // A scheduled run to show what was new at it (the link in an alert, task 37).
    ...(typeof s.run === 'string' && ULID.test(s.run) ? { run: s.run } : {}),
    // The findings whose AI draft the document should start and follow (the creation flow hands over here).
    ...(typeof s.draft === 'string' && ULID.test(s.draft) ? { draft: s.draft } : {}),
    // Opens the Advanced drawer (when the preference is on).
    ...(s.advanced === true || s.advanced === 1 || s.advanced === '1' ? { advanced: true } : {}),
  }),
  component: ReportPage,
});

function ReportPage() {
  const { id } = Route.useParams();
  const { template: templateId, run: runId, draft: draftId, advanced } = Route.useSearch();
  const isNew = id === 'new';
  const { data: snap, error: schemaError } = useSchema();
  const saved = useReport(isNew ? null : id);
  const templates = useReportTemplates();
  const template = templateId ? templates.data?.templates.find(t => t.id === templateId) : undefined;
  const back = (
    <Button size="sm" variant="ghost" asChild>
      <Link to="/reports">
        <ArrowLeft aria-hidden="true" /> All reports
      </Link>
    </Button>
  );
  if (saved.isError || schemaError)
    return (
      <>
        <PageHeader title="Report" actions={back} />
        <ErrorState
          error={saved.error ?? schemaError}
          title="This report couldn’t be opened"
          onRetry={() => saved.refetch()}
          learnMore="build-a-report"
        />
      </>
    );
  if (!snap || (!isNew && !saved.data) || (templateId && templates.isPending))
    return (
      <div aria-busy="true" className="space-y-3">
        <span className="sr-only">Loading…</span>
        <div className="h-8 w-64 animate-pulse rounded-md bg-muted" />
        <div className="h-40 animate-pulse rounded-md bg-muted" />
      </div>
    );
  if (isNew)
    return (
      <AdvancedBuilder
        key={`new:${templateId ?? ''}`}
        snap={snap}
        report={null}
        templateId={template?.id ?? null}
        templateName={template?.name ?? null}
        templateSections={template?.sections ?? []}
        back={back}
      />
    );
  return (
    <ReportDocumentView
      key={id}
      report={saved.data as ReportDoc}
      snap={snap}
      runId={runId ?? null}
      draftId={draftId ?? null}
      openAdvanced={!!advanced}
      back={back}
    />
  );
}
